/**
 * What the "Magazyn" step works out from the dosing (#692 rev B): whether
 * the drug is given here or handed over, and how much leaves the shelf —
 * one dose in the Product's own Unit, or what is handed over (a whole
 * course, or one administration) rounded up to a whole Unit and, for a
 * Product dispensed in packages, to whole packages.
 * Hook-free, so the form's tests reach it without the organization context.
 */
import {
  GetProfileMedicationsDtoDurationTypeEnum,
  GetProfileMedicationsDtoFormEnum,
  GetProfileMedicationsDtoFrequencyTypeEnum,
  WarehouseDispenseMode,
  WarehouseUnit,
} from 'woofwoof-api';
import {
  StockMeasure,
  parseStockQuantity,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { stockUnitForDoseForm } from './medicationDrugData';
import { StockHandoverChoice } from './medicationStockValues';

type FrequencyType = GetProfileMedicationsDtoFrequencyTypeEnum;
type DurationType = GetProfileMedicationsDtoDurationTypeEnum;

export type { WarehouseDispenseMode };

const COURSE_FREQUENCIES = [
  'TIMES_PER_DAY',
  'EVERY_N_HOURS',
  'EVERY_N_DAYS',
  'TIMES_PER_WEEK',
  'TIMES_PER_MONTH',
] as const;
const COURSE_DURATIONS = ['DAYS', 'WEEKS'] as const;

export type CourseFrequencyType = (typeof COURSE_FREQUENCIES)[number];
export type CourseDurationType = (typeof COURSE_DURATIONS)[number];

/** Any repeated frequency means the owner gives the drug at home. */
const REPEATED: readonly FrequencyType[] = [...COURSE_FREQUENCIES, 'AS_NEEDED'];

/** Administrations per day as a fraction, so a course multiplies before it divides. */
const PER_DAY: Record<
  CourseFrequencyType,
  (value: number) => { times: number; per: number }
> = {
  TIMES_PER_DAY: (value) => ({ times: value, per: 1 }),
  EVERY_N_HOURS: (value) => ({ times: 24, per: value }),
  EVERY_N_DAYS: (value) => ({ times: 1, per: value }),
  TIMES_PER_WEEK: (value) => ({ times: value, per: 7 }),
  TIMES_PER_MONTH: (value) => ({ times: value, per: 30 }),
};

const DAYS_PER: Record<CourseDurationType, number> = { DAYS: 1, WEEKS: 7 };

/** The dose and course as the form holds them; a typed number arrives as its string. */
export interface StockIssueDosing {
  dosagePending: boolean;
  form: GetProfileMedicationsDtoFormEnum | '';
  quantityValue: number | string;
  frequencyType: FrequencyType | '';
  frequencyValue: number | string;
  durationType: DurationType | '';
  durationValue: number | string;
}

/** What the computation needs of the Warehouse Product; its detail DTO fits. */
export interface StockIssueProduct {
  unit: WarehouseUnit;
  unitsPerPackage: number;
  /** Absent on a Product never set: whole packages. */
  dispenseMode?: WarehouseDispenseMode;
}

export interface StockIssueDose {
  kind: 'DOSE';
  dose: number;
}

/** One administration handed over (P7B-R10): whole Units, packages as dispensed. */
export interface StockIssueDoseHandedOver {
  kind: 'DOSE_HANDED_OVER';
  dose: number;
  units: number;
  /** Whole packages for a Product dispensed in packages; null by the unit. */
  packages: number | null;
}

export interface StockIssueCourse {
  kind: 'COURSE';
  dose: number;
  frequencyType: CourseFrequencyType;
  frequencyValue: number;
  durationType: CourseDurationType;
  durationValue: number;
  /** The course in the Product's Units, rounded up to a whole Unit. */
  units: number;
  /** Whole packages for a Product dispensed in packages; null by the unit. */
  packages: number | null;
}

/** How the quantity was reached, for the line under "Ilość". */
export type StockIssueBasis =
  | StockIssueDose
  | StockIssueDoseHandedOver
  | StockIssueCourse;

export interface ComputedStockIssue {
  quantity: number | '';
  measure: StockMeasure;
  /** Null when the dosing does not say how much. */
  basis: StockIssueBasis | null;
}

const isCourseFrequency = (value: string): value is CourseFrequencyType =>
  (COURSE_FREQUENCIES as readonly string[]).includes(value);

const isCourseDuration = (value: string): value is CourseDurationType =>
  (COURSE_DURATIONS as readonly string[]).includes(value);

/** Up to a whole Unit; a product of floats that is whole stays whole (0.1 × 3 × 10). */
const wholeUnitsUp = (value: number): number =>
  Math.ceil(Number(value.toFixed(6)));

/** Given once: a single dose, or no frequency to repeat it by. */
const givenOnce = ({
  frequencyType,
  durationType,
}: Pick<StockIssueDosing, 'frequencyType' | 'durationType'>): boolean =>
  durationType === 'SINGLE_DOSE' || !frequencyType;

/**
 * "Wydany opiekunowi" for any repeated frequency; otherwise — a single
 * dose, no frequency, or one written by hand — "Podany w placówce". Not for
 * an Rx drug (P7B-R10): given here, it goes onto the Drug Record Card as
 * administered, so '' leaves the answer to the vet.
 */
export function inferStockHandover(
  dosing: Pick<StockIssueDosing, 'frequencyType' | 'durationType'>,
  isRx: boolean,
): StockHandoverChoice {
  const { frequencyType } = dosing;
  if (!givenOnce(dosing) && frequencyType && REPEATED.includes(frequencyType)) {
    return 'HANDED_OVER';
  }
  return isRx ? '' : 'ADMINISTERED';
}

/**
 * The quantity for `handover`, in the Product's Units — only when the dose
 * is counted in them (`stockUnitForDoseForm`) and is not still to be
 * decided, and once there is a handover to count for. A drug given here
 * takes one dose; one handed over takes the course (dose × administrations
 * per day × days), which only a frequency with a number and a duration in
 * days or weeks can say — or, given once, that one administration. What is
 * handed over is rounded up to a whole Unit and, for a Product dispensed
 * in packages, to whole packages.
 */
export function computeStockIssue(
  dosing: StockIssueDosing,
  handover: StockHandoverChoice,
  product: StockIssueProduct | undefined,
): ComputedStockIssue {
  const handedOver = handover === 'HANDED_OVER';
  const inPackages =
    handedOver && (product?.dispenseMode ?? 'PACKAGES') === 'PACKAGES';
  const measure: StockMeasure = inPackages ? 'PACKAGE' : 'UNIT';
  const none: ComputedStockIssue = { quantity: '', measure, basis: null };
  const dose = dosing.dosagePending
    ? null
    : parseStockQuantity(dosing.quantityValue);
  const doseUnit = stockUnitForDoseForm(dosing.form);
  if (
    !handover ||
    !product ||
    dose === null ||
    !doseUnit ||
    doseUnit !== product.unit
  ) {
    return none;
  }
  if (!handedOver) {
    return { quantity: dose, measure: 'UNIT', basis: { kind: 'DOSE', dose } };
  }
  const packagesOf = (units: number): number | null =>
    inPackages ? Math.ceil(units / product.unitsPerPackage) : null;
  if (givenOnce(dosing)) {
    const units = wholeUnitsUp(dose);
    const packages = packagesOf(units);
    return {
      quantity: packages ?? units,
      measure,
      basis: { kind: 'DOSE_HANDED_OVER', dose, units, packages },
    };
  }
  const { frequencyType, durationType } = dosing;
  const frequencyValue = parseStockQuantity(dosing.frequencyValue);
  const durationValue = parseStockQuantity(dosing.durationValue);
  if (
    !isCourseFrequency(frequencyType) ||
    !isCourseDuration(durationType) ||
    frequencyValue === null ||
    durationValue === null
  ) {
    return none;
  }
  const { times, per } = PER_DAY[frequencyType](frequencyValue);
  const units = wholeUnitsUp(
    (dose * times * durationValue * DAYS_PER[durationType]) / per,
  );
  const packages = packagesOf(units);
  return {
    quantity: packages ?? units,
    measure,
    basis: {
      kind: 'COURSE',
      dose,
      frequencyType,
      frequencyValue,
      durationType,
      durationValue,
      units,
      packages,
    },
  };
}

/** A quantity and measure the host set, to tell it apart from the vet's. */
export interface StockQuantityDefault {
  quantity: number | '';
  measure: StockMeasure;
}

/**
 * The field may take a new computed value: it is empty, or still holds the
 * one set before. A quantity the vet typed — or a saved taking's, which
 * nothing computed — never does.
 */
export function holdsStockDefault(
  quantity: number | string,
  measure: StockMeasure,
  previous: StockQuantityDefault | null,
): boolean {
  const current = parseStockQuantity(quantity);
  if (current === null) return true;
  return (
    previous !== null &&
    previous.quantity !== '' &&
    current === previous.quantity &&
    measure === previous.measure
  );
}
